import { useCallback, useEffect, useState } from 'react'
import { api } from '../services/api'
import { trackEvent } from '../utils/analytics'
import { isLegacyOrigin } from '../utils/canonicalOrigin'
import { isStandalone } from '../utils/standalone'
import { isIOSSafari } from './useInstallPrompt'

/**
 * Web Push opt-in, per device.
 *
 * One subscription per browser profile is the whole preference model: it exists
 * or it does not. Everything that could hide the feature is resolved here so
 * every surface inherits the same gate — in particular the domain-migration
 * rule, since a subscription is bound to the origin of the service-worker
 * registration that minted it and one minted on the legacy host would die at
 * sunset, unmigratable (plans/push-notifications.md §4).
 */

const IS_MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true'

export interface PushNotificationsState {
  /** Platform, origin and server all say push is possible here. */
  isSupported: boolean
  /** iOS browser tab: the platform allows push, but only once installed. */
  needsInstall: boolean
  permission: NotificationPermission
  isSubscribed: boolean
  isWorking: boolean
  /** MUST be called from a click handler — iOS auto-denies outside a gesture. */
  subscribe: () => Promise<boolean>
  unsubscribe: () => Promise<boolean>
}

/**
 * The browser wants the VAPID key as raw bytes, not as the base64url we serve.
 * Typed as Uint8Array<ArrayBuffer> because `applicationServerKey` rejects the
 * possibly-shared buffer the plain Uint8Array type admits.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

/**
 * The active service-worker registration, or null.
 *
 * `serviceWorker.ready` never settles when nothing is registered — the normal
 * state under `vite dev`, where the SW is not built — so it is only awaited
 * once a registration is known to exist. Otherwise a click on "Ativar
 * notificações" would spin forever instead of failing.
 */
async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration()
  if (!existing) return null
  return navigator.serviceWorker.ready
}

function hasPushApi(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

// The server probe is per-page-load, not per-hook: the answer is a deploy-time
// property of the backend, and the Settings card should not re-ask it every
// time it mounts. `null` = not asked yet, `false` = asked and push is off.
let publicKeyPromise: Promise<string | null> | null = null

function fetchPublicKey(): Promise<string | null> {
  if (!publicKeyPromise) {
    publicKeyPromise = api.vision.push
      .getPublicKey()
      .then((res) => (res.success && res.data?.publicKey ? res.data.publicKey : null))
      .catch(() => null)
  }
  return publicKeyPromise
}

export function usePushNotifications(): PushNotificationsState {
  const platformSupported = hasPushApi() && !isLegacyOrigin() && !IS_MOCK_AUTH
  const needsInstall = platformSupported && isIOSSafari() && !isStandalone()

  const [serverEnabled, setServerEnabled] = useState<boolean | null>(null)
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    hasPushApi() ? Notification.permission : 'default'
  )
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isWorking, setIsWorking] = useState(false)

  // Ask the backend whether push exists at all, and read the current
  // subscription off the registration. `serviceWorker.ready` resolves to the
  // same registration useServiceWorker holds, so nothing needs to be shared.
  useEffect(() => {
    if (!platformSupported || needsInstall) return
    let cancelled = false

    fetchPublicKey().then((key) => {
      if (!cancelled) setServerEnabled(Boolean(key))
    })

    getRegistration()
      .then((registration) => registration?.pushManager.getSubscription() ?? null)
      .then((subscription) => {
        if (!cancelled) setIsSubscribed(Boolean(subscription))
      })
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [platformSupported, needsInstall])

  const subscribe = useCallback(async () => {
    setIsWorking(true)
    trackEvent('btv_push_enable_click')
    try {
      const publicKey = await fetchPublicKey()
      if (!publicKey) return false

      // Both of these must run inside the caller's gesture: iOS auto-denies a
      // permission request that is not attributable to a user action.
      const result = await Notification.requestPermission()
      setPermission(result)
      if (result !== 'granted') {
        trackEvent('btv_push_permission_denied')
        return false
      }
      trackEvent('btv_push_permission_granted')

      const registration = await getRegistration()
      if (!registration) return false

      const subscription = await registration.pushManager.subscribe({
        // Required by every browser, and true anyway: our SW always shows one.
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })

      const response = await api.vision.push.subscribe({
        subscription: subscription.toJSON(),
        userAgent: navigator.userAgent,
        appHost: window.location.hostname,
      })

      if (!response.success) {
        // Never leave a browser-side subscription the server does not know
        // about: it would receive nothing and the UI would claim otherwise.
        await subscription.unsubscribe().catch(() => undefined)
        return false
      }

      setIsSubscribed(true)
      trackEvent('btv_push_subscribed')
      return true
    } catch {
      return false
    } finally {
      setIsWorking(false)
    }
  }, [])

  const unsubscribe = useCallback(async () => {
    setIsWorking(true)
    try {
      const registration = await getRegistration()
      const subscription = await registration?.pushManager.getSubscription()
      if (!subscription) {
        setIsSubscribed(false)
        return true
      }

      // Server first: if the browser-side step then fails, the row is already
      // gone and the next send prunes the remnant on its 410.
      await api.vision.push.unsubscribe({ endpoint: subscription.endpoint })
      await subscription.unsubscribe().catch(() => undefined)

      setIsSubscribed(false)
      trackEvent('btv_push_unsubscribed')
      return true
    } catch {
      return false
    } finally {
      setIsWorking(false)
    }
  }, [])

  return {
    isSupported: platformSupported && !needsInstall && serverEnabled === true,
    needsInstall,
    permission,
    isSubscribed,
    isWorking,
    subscribe,
    unsubscribe,
  }
}
